import React, { useState } from "react";
import { useParams } from "react-router-dom";
import { Table, Tag, Tooltip } from "antd";
import LoadingComponent from "components/loading-component";
import LineItemsModal from "./line-items-modal";
import {
  CheckCircleFilled,
  DatabaseOutlined,
  SendOutlined,
} from "@ant-design/icons";

const PurchasedOrdersTable = ({
  data = [],
  materialsData = [],
  onDataUpdate,
  showTransferModal,
  searchText = "",
  loading = false,
  onSelectionChange,
  selectedItems,
  getData,
  accessLevel,
}) => {
  const { company_id, id } = useParams();
  const [selectedRows, setSelectedRows] = useState([]);
  const [isPostLineItemVisible, setIsPostLineItemVisible] = useState(false);
  const [readOnlyMode, setReadOnlyMode] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);

  // Status color mapping
  const getStatusColor = (status) => {
    const statusColors = {
      Draft: "default",
      Processing: "processing",
      Submitted: "blue",
      "Partially Received": "orange",
      Received: "green",
      Approved: "success",
      Closed: "red",
    };
    return statusColors[status] || "default";
  };

  // Function to get material name from materialsData - handles single ID
  const getMaterialName = (itemID) => {
    if (itemID === "-1" || !itemID) {
      return "N/A";
    }

    // Check if materialsData is an object with items property
    const materials = materialsData?.items || materialsData || {};
    const material = materials[itemID];

    return material?.materialName || "";
  };

  // Function to get multiple material names - handles array of IDs
  const getMaterialNames = (itemIDs) => {
    if (!itemIDs) return ["N/A"];

    // Handle single itemID (backward compatibility)
    if (!Array.isArray(itemIDs)) {
      return [getMaterialName(itemIDs)];
    }

    // Handle array of itemIDs
    return itemIDs
      .map((itemID) => getMaterialName(itemID))
      .filter((name) => name && name !== "N/A");
  };

  // Filter data based on search text
  const filteredData = data.filter((item) => {
    const search = searchText.toLowerCase();

    // Get all material names for search
    const materialNames = getMaterialNames(
      item.itemID || item.itemIDs || item.materials
    );
    const materialSearchText = materialNames.join(" ").toLowerCase();

    return (
      item.title?.toLowerCase().includes(search) ||
      item.number?.toLowerCase().includes(search) ||
      item.status?.toLowerCase().includes(search) ||
      materialSearchText.includes(search)
    );
  });

  const handleRowClick = (record) => {
    // if (record.inventoryStatus === "Sent") {
    // setReadOnlyMode(true);
    // return;
    // }

    const key = record.purchaseOrderID;

    setSelectedItem(record);
    setIsPostLineItemVisible(true);
  };

  // Base columns
  const baseColumns = [
    {
      title: "Title",
      dataIndex: "title",
      key: "title",
      width: 250,
      sorter: (a, b) => (a.title || "").localeCompare(b.title || ""),
      render: (text, record) => (
        <div className=" d-flex justify-content-between align-items-center">
          <div>{text}</div>
          {record.inventoryStatus === "Sent" && (
            <Tooltip title="Transferred to Inventory">
              <CheckCircleFilled className=" opacity-50 text-body fs-6" />
            </Tooltip>
          )}
        </div>
      ),
    },
    {
      title: "Number",
      dataIndex: "number",
      key: "number",
      width: 150,
      sorter: (a, b) => (a.number || "").localeCompare(b.number || ""),
    },
    {
      title: "Accounting Method",
      dataIndex: "accounting_method",
      key: "accountingMethod",
      width: 150,
      render: (value) => (value === "amount" ? "Amount Based" : "Unit/Qty"),
      sorter: (a, b) =>
        (a.accounting_method || "").localeCompare(b.accounting_method || ""),
    },
    {
      title: "Material",
      dataIndex: "materials",
      key: "materials",
      width: 200,
      sorter: (a, b) => {
        const materialNamesA = getMaterialNames(
          a.itemID || a.itemIDs || a.materials
        );
        const materialNamesB = getMaterialNames(
          b.itemID || b.itemIDs || b.materials
        );
        const materialA = materialNamesA.join(", ");
        const materialB = materialNamesB.join(", ");
        return materialA.localeCompare(materialB);
      },
      render: (item, record) => {
        const materialNames = getMaterialNames(
          record.itemID || record.itemIDs || record.materials
        );
        if (materialNames.length <= 3) {
          // Show all tags if 3 or fewer
          return (
            <div style={{ display: "flex", flexWrap: "wrap", gap: "4px" }}>
              {materialNames.map((materialName, index) => (
                <Tag
                  key={index}
                  className="rounded-5 fw-bold"
                  bordered={false}
                  color="blue"
                  style={{ margin: 0 }}
                >
                  {materialName}
                </Tag>
              ))}
            </div>
          );
        } else {
          // Show first 2 tags + count of remaining
          return (
            <div
              style={{
                display: "flex",
                flexWrap: "wrap",
                gap: "4px",
                alignItems: "center",
              }}
            >
              {materialNames.slice(0, 2).map((materialName, index) => (
                <Tag
                  key={index}
                  className="rounded-5 fw-bold"
                  bordered={false}
                  color="blue"
                  style={{ margin: 0 }}
                >
                  {materialName}
                </Tag>
              ))}
              <Tooltip title={materialNames.slice(2).join(", ")}>
                <Tag
                  className="rounded-5 fw-bold"
                  bordered={false}
                  color="blue"
                >
                  +{materialNames.length - 2} more
                </Tag>
              </Tooltip>
            </div>
          );
        }
      },
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      width: 150,
      sorter: (a, b) => (a.status || "").localeCompare(b.status || ""),
      filters: [
        { text: "Draft", value: "Draft" },
        { text: "Processing", value: "Processing" },
        { text: "Submitted", value: "Submitted" },
        { text: "Partially Received", value: "Partially Received" },
        { text: "Received", value: "Received" },
        { text: "Approved", value: "Approved" },
        { text: "Closed", value: "Closed" },
      ],
      onFilter: (value, record) => record.status === value,
      render: (status, record) => (
        <Tag
          className="rounded-5 fw-bold"
          bordered={false}
          color={
            record.inventoryStatus === "sent"
              ? "default"
              : getStatusColor(status)
          }
        >
          {status}
        </Tag>
      ),
    },
  ];

  return (
    <>
      <Table
        dataSource={filteredData}
        scroll={{ x: "max-content" }}
        columns={baseColumns}
        rowKey={(record) => record.purchaseOrderID}
        loading={{
          spinning: loading,
          indicator: <LoadingComponent />,
        }}
        size="large"
        bordered
        pagination={false}
        // rowSelection={rowSelection}
        onRow={(record) => ({
          onClick: () => handleRowClick(record),
          style: {
            cursor: "pointer",
            // opacity: record.inventoryStatus === "Sent" ? 0.8 : 1,
          },
        })}
      />

      <LineItemsModal
        record={selectedItem}
        isPostLineItemVisible={isPostLineItemVisible}
        setIsPostLineItemVisible={setIsPostLineItemVisible}
        projectID={selectedItem?.projectID}
        setShouldReload={getData}
        isTransferMode={true}
        materialsData={materialsData}
        readOnlyMode={selectedItem?.inventoryStatus === "Sent" ? true : false}
        accountingMethod={selectedItem?.accounting_method}
        updateMaterials={getData}
        accessLevel={accessLevel}
      />
    </>
  );
};

export default PurchasedOrdersTable;
